import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import Sidebar from "@/components/Sidebar";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import { useEntitlement } from "@/context/EntitlementContext";
import { toast } from "sonner";
import { Crown, Trash2, HelpCircle, ShieldCheck, FileText, ExternalLink, Settings2, Gift, Bell, BellOff, Copy, Share2, MessageCircle, Twitter, Mail } from "lucide-react";
import { ensurePushRegistered, disablePush } from "@/lib/push";

export default function Settings() {
  const { user, logout } = useAuth();
  const { active: isPro, expires_at, source, refresh } = useEntitlement();
  const nav = useNavigate();
  const [deleting, setDeleting] = useState(false);
  const [checkout, setCheckout] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [refStats, setRefStats] = useState(null);
  const [pushEnabled, setPushEnabled] = useState(typeof Notification !== "undefined" && Notification.permission === "granted");

  useEffect(() => {
    refresh();
    api.get("/referrals/me").then((r) => setRefStats(r.data)).catch(() => {});
  }, [refresh]);

  const togglePush = async () => {
    if (pushEnabled) {
      await disablePush(); setPushEnabled(false); toast.info("Notifications turned off");
    } else {
      const res = await ensurePushRegistered();
      if (res.ok) { setPushEnabled(true); toast.success("You'll get pinged when replies arrive"); }
      else if (res.reason === "denied") toast.error("Notification permission blocked in your browser settings");
      else if (res.reason === "unsupported") toast.error("This browser doesn't support web push");
    }
  };

  const copyRefLink = () => {
    if (!refStats?.referral_code) return;
    const link = `${window.location.origin}/register?ref=${refStats.referral_code}`;
    navigator.clipboard.writeText(link);
    toast.success("Referral link copied");
  };

  const upgrade = async () => {
    setCheckout(true);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: "aiapply_pro_monthly", origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setCheckout(false); }
  };

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post("/payments/portal", { return_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setPortalLoading(false); }
  };

  const deleteAccount = async () => {
    setDeleting(true);
    try {
      await api.delete("/auth/me");
      localStorage.removeItem("applyloop_token");
      toast.success("Your account and data have been permanently deleted.");
      await logout();
      nav("/");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setDeleting(false); }
  };

  return (
    <div className="flex min-h-screen bg-[#FDFDFB]">
      <Sidebar />
      <main className="flex-1 max-w-4xl mx-auto px-6 md:px-10 py-10 w-full">
        <div className="mb-8">
          <div className="overline">Settings</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-2">Your account</h1>
        </div>

        <Card className="p-6 border-[#E2E8E4]" data-testid="account-card">
          <div className="overline">Account</div>
          <div className="mt-3 space-y-1 text-sm">
            <div><span className="text-[#5C6661]">Name:</span> <span className="font-medium">{user?.name}</span></div>
            <div><span className="text-[#5C6661]">Email:</span> <span className="font-medium">{user?.email}</span></div>
          </div>
        </Card>

        <Card className="p-6 border-[#E2E8E4] mt-6" data-testid="plan-card">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="overline">Plan</div>
              <div className="mt-2 flex items-center gap-2">
                {isPro ? (
                  <Badge className="bg-[#2D4A3E] text-white border-transparent"><Crown className="w-3 h-3 mr-1" /> JobKnocker Pro</Badge>
                ) : (
                  <Badge variant="secondary" className="bg-[#F2F4F3] text-[#5C6661] border-transparent">Free</Badge>
                )}
                {isPro && expires_at && <span className="text-xs text-[#5C6661]">Renews {new Date(expires_at).toLocaleDateString()} · via {source}</span>}
              </div>
            </div>
            {!isPro ? (
              <Button onClick={upgrade} disabled={checkout} className="rounded-full bg-[#2D4A3E] hover:bg-[#3B5F50]" data-testid="upgrade-btn"><Crown className="w-4 h-4 mr-2" /> {checkout ? "Redirecting…" : "Upgrade to Pro — £4.99/mo"}</Button>
            ) : source === "stripe" ? (
              <Button onClick={openPortal} disabled={portalLoading} variant="outline" className="rounded-full border-[#2D4A3E] text-[#2D4A3E] hover:bg-[#E7F0EB]" data-testid="manage-subscription-btn">
                <Settings2 className="w-4 h-4 mr-2" /> {portalLoading ? "Opening…" : "Manage subscription"}
              </Button>
            ) : source === "apple" ? (
              <a href="https://apps.apple.com/account/subscriptions" target="_blank" rel="noreferrer" data-testid="manage-apple-btn">
                <Button variant="outline" className="rounded-full border-[#2D4A3E] text-[#2D4A3E] hover:bg-[#E7F0EB]">
                  <Settings2 className="w-4 h-4 mr-2" /> Manage on Apple
                </Button>
              </a>
            ) : (
              <div className="text-xs text-[#5C6661] max-w-xs text-right">Managed by Apple — open Settings → Apple ID → Subscriptions on your iPhone to cancel or update.</div>
            )}
          </div>
        </Card>

        <Card className="p-6 border-[#E2E8E4] mt-6" data-testid="referrals-card">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="overline flex items-center gap-1.5"><Gift className="w-3 h-3" /> Invite friends</div>
              <div className="mt-2 text-sm text-[#121413]">Give a friend JobKnocker, and get a <strong>free month of Pro</strong> when they upgrade.</div>
              {refStats?.referral_code && (
                <>
                  <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#F2F4F3] border border-[#E2E8E4] px-4 py-2 text-sm">
                    <span className="text-[#5C6661]">Your code:</span>
                    <span className="font-mono font-semibold text-[#2D4A3E]" data-testid="referral-code">{refStats.referral_code}</span>
                    <button onClick={copyRefLink} className="ml-1 p-1 rounded hover:bg-white" data-testid="copy-ref-link"><Copy className="w-3.5 h-3.5 text-[#5C6661]" /></button>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      onClick={async () => {
                        const link = `${window.location.origin}/register?ref=${refStats.referral_code}`;
                        const text = `Try JobKnocker — it sends your CV to companies that fit your profile. Use my code ${refStats.referral_code} and we both get a free month of Pro.`;
                        if (navigator.share) { try { await navigator.share({ title: "JobKnocker", text, url: link }); } catch (e) { console.info("Native share cancelled or unavailable.", e); } }
                        else { navigator.clipboard.writeText(`${text} ${link}`); toast.success("Share text copied"); }
                      }}
                      className="inline-flex items-center gap-1.5 text-xs rounded-full border border-[#E2E8E4] bg-white px-3 py-1.5 hover:bg-[#F2F4F3]"
                      data-testid="share-native"
                    ><Share2 className="w-3 h-3" /> Share</button>
                    <a href={`https://wa.me/?text=${encodeURIComponent(`Try JobKnocker with my code ${refStats.referral_code} — free month of Pro for us both: ${window.location.origin}/register?ref=${refStats.referral_code}`)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs rounded-full border border-[#E2E8E4] bg-white px-3 py-1.5 hover:bg-[#F2F4F3]" data-testid="share-whatsapp"><MessageCircle className="w-3 h-3 text-[#25D366]" /> WhatsApp</a>
                    <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(`I'm using JobKnocker to send my CV to companies that fit. Use my code ${refStats.referral_code} for a free month of Pro:`)}&url=${encodeURIComponent(`${window.location.origin}/register?ref=${refStats.referral_code}`)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs rounded-full border border-[#E2E8E4] bg-white px-3 py-1.5 hover:bg-[#F2F4F3]" data-testid="share-x"><Twitter className="w-3 h-3" /> X</a>
                    <a href={`mailto:?subject=${encodeURIComponent("Try JobKnocker — free month of Pro")}&body=${encodeURIComponent(`I've been using JobKnocker to send my CV to companies that fit my profile — even ones not actively hiring.\n\nUse my referral code ${refStats.referral_code} when you sign up and we both get a free month of Pro:\n${window.location.origin}/register?ref=${refStats.referral_code}`)}`} className="inline-flex items-center gap-1.5 text-xs rounded-full border border-[#E2E8E4] bg-white px-3 py-1.5 hover:bg-[#F2F4F3]" data-testid="share-email"><Mail className="w-3 h-3" /> Email</a>
                  </div>
                </>
              )}
              {refStats && (
                <div className="mt-3 text-xs text-[#5C6661]">
                  {refStats.invited_count} invited · {refStats.converted_count} upgraded · <span className="text-[#2D4A3E] font-medium">{refStats.months_earned} free month{refStats.months_earned === 1 ? "" : "s"} earned</span> · <Link to="/leaderboard" className="text-[#2D4A3E] underline" data-testid="leaderboard-link">See leaderboard</Link>
                </div>
              )}
            </div>
          </div>
        </Card>

        <Card className="p-6 border-[#E2E8E4] mt-6" data-testid="notifications-card">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="overline flex items-center gap-1.5"><Bell className="w-3 h-3" /> Notifications</div>
              <div className="mt-2 text-sm text-[#121413]">Get an instant browser ping when a company replies to your application.</div>
              <div className="text-xs text-[#5C6661] mt-1">Web push works while this browser is open on any device.</div>
            </div>
            <Button onClick={togglePush} variant="outline" className="rounded-full border-[#E2E8E4]" data-testid="toggle-push-btn">
              {pushEnabled ? <><BellOff className="w-4 h-4 mr-2" /> Turn off notifications</> : <><Bell className="w-4 h-4 mr-2" /> Enable notifications</>}
            </Button>
          </div>
        </Card>

        <Card className="p-6 border-[#E2E8E4] mt-6">
          <div className="overline">Legal & help</div>
          <div className="mt-3 grid sm:grid-cols-2 gap-2">
            <Link to="/privacy" className="flex items-center justify-between p-3 rounded-lg hover:bg-[#F2F4F3]" data-testid="link-privacy"><span className="text-sm inline-flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#5C6661]" /> Privacy Policy</span><ExternalLink className="w-3.5 h-3.5 text-[#5C6661]" /></Link>
            <Link to="/terms" className="flex items-center justify-between p-3 rounded-lg hover:bg-[#F2F4F3]" data-testid="link-terms"><span className="text-sm inline-flex items-center gap-2"><FileText className="w-4 h-4 text-[#5C6661]" /> Terms</span><ExternalLink className="w-3.5 h-3.5 text-[#5C6661]" /></Link>
            <Link to="/#how" className="flex items-center justify-between p-3 rounded-lg hover:bg-[#F2F4F3]"><span className="text-sm inline-flex items-center gap-2"><HelpCircle className="w-4 h-4 text-[#5C6661]" /> How it works</span><ExternalLink className="w-3.5 h-3.5 text-[#5C6661]" /></Link>
          </div>
        </Card>

        <Card className="p-6 border-[#F5C1BE] mt-6 bg-[#FFF6F5]" data-testid="danger-zone">
          <div className="overline text-[#C53030]">Danger zone</div>
          <div className="mt-3 flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="text-sm font-medium">Delete your account</div>
              <div className="text-xs text-[#5C6661] max-w-xl mt-1">Permanently removes your account, CV, discovered companies, sent history and SMTP settings. This action cannot be undone.</div>
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="rounded-full border-[#C53030] text-[#C53030] hover:bg-[#FCE8E6]" data-testid="delete-account-btn"><Trash2 className="w-4 h-4 mr-2" /> Delete account</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete your JobKnocker account?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will immediately and permanently delete your account and all associated data (CV, companies, history, SMTP config). If you have an active subscription, remember to cancel it separately with Apple or Stripe.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="cancel-delete">Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={deleteAccount} disabled={deleting} className="bg-[#C53030] hover:bg-[#a92727]" data-testid="confirm-delete">{deleting ? "Deleting…" : "Yes, delete everything"}</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </Card>
      </main>
    </div>
  );
}
